import { Badge } from '@liveauctioneers/hammer-ui-core/badge';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import useVisibilitySensor from '@rooks/use-visibility-sensor';
import { useCallback, useEffect, useRef, useState } from 'react';

import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { usePrevious } from '@/hooks/usePrevious';
import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryIndex,
    getItemSummaryIsSold,
    getItemSummaryPromoted,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardNavigationTrigger } from '@/utils/amplitude/analytics';
import { LotRecommendationTrigger } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import noop from '@/utils/noop';

import CardHouseAndRating from './components/CardHouseAndRating/CardHouseAndRating';
import CardImage from './components/CardImage/CardImage';
import { CardInfo } from './components/CardInfo/CardInfo';
import { CardPrice } from './components/CardPrice/CardPrice';

const CategorySearchCard: ItemCardComponent = ({
    className,
    dimension,
    hideCardMenu,
    itemId,
    lazyLoadImage,
    pagePosition,
    recommendationPosition,
    recommendationType,
    trackClick = noop,
    trackVisible = noop,
}) => {
    const formatMessage = useAppFormatMessage();
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const { onNavigateInitiated } = useItemCardNavigation(itemId);

    // we track the position in search results, to know if user clicks 1st item, 5th, etc
    const trackingPosition = pagePosition || index;

    const [cardHover, setCardHover] = useState(false);
    const [linkHover, setLinkHover] = useState(false);
    const isMobile = useIsMobileLayout();
    const cardRef = useRef(null);
    const [hasVisibilityBeenTracked, setHasVisibilityBeenTracked] = useState(false);
    const { isVisible } = useVisibilitySensor(cardRef, {
        intervalCheck: false,
        resizeCheck: true,
        scrollCheck: true,
    });

    const previousIsVisible = usePrevious(isVisible);

    useEffect(() => {
        const isNewlyVisible = !previousIsVisible && isVisible;
        if (isNewlyVisible && !hasVisibilityBeenTracked) {
            trackVisible?.(itemId, promoted);
            setHasVisibilityBeenTracked(true);
        }
    }, [isVisible, itemId, previousIsVisible, promoted, trackVisible, hasVisibilityBeenTracked]);

    const enterHoverState = useCallback(() => {
        setCardHover(true);
    }, []);

    const leaveHoverState = useCallback(() => {
        setCardHover(false);
    }, []);

    const enterLinkHoverState = useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = useCallback(() => {
        setLinkHover(false);
    }, []);

    const onCardClick = useCallback(() => {
        if (!isSold) {
            onNavigateInitiated(ItemCardNavigationTrigger.ImageClick, { position: trackingPosition });
        }
        trackClick(itemId, trackingPosition);
    }, [isSold, trackClick, itemId, trackingPosition, onNavigateInitiated]);

    const onRecommendationClick = useRecommendationTracking({
        itemId,
        recommendationPosition: recommendationPosition ?? 0,
        recommendationType,
    });

    return (
        <article
            className={cn(!dimension && 'w-auto', className)}
            data-testid={`category-search-card-${itemId}`}
            onClick={onCardClick}
            onMouseEnter={enterHoverState}
            onMouseLeave={leaveHoverState}
            ref={cardRef}
            style={dimension ? { width: `${dimension}px` } : undefined}
        >
            <section
                className="relative grid min-w-0 [grid-template-rows:min-content_22px_min-content_4px_min-content_4px_min-content_8px_min-content_8px_min-content] justify-center [grid-template-areas:'itemimage'_'.'_'iteminfo'_'.'_'title'_'.'_'cardhouseandrating'_'.'_'currentbidprice'_'.'_'easyshipping']"
                style={{ gridTemplateColumns: dimension ? `${dimension}px` : '1fr' }}
            >
                {promoted && (
                    <Badge
                        className="absolute top-xs left-xs"
                        color="neutral"
                        size="sm"
                    >
                        {formatMessage(['featured'])}
                    </Badge>
                )}
                <div className="overflow-hidden rounded-md border-[1px] border-solid border-border-tertiary">
                    <CardImage
                        canShowEasyShipping
                        enterLinkHoverState={enterLinkHoverState}
                        isMobile={isMobile}
                        itemId={itemId}
                        lazyLoadImage={lazyLoadImage}
                        leaveLinkHoverState={leaveLinkHoverState}
                        onNavigate={() => {
                            onNavigateInitiated(ItemCardNavigationTrigger.ImageClick, {
                                position: trackingPosition,
                            });
                            onRecommendationClick(LotRecommendationTrigger.ImageClick);
                        }}
                    />
                </div>
                <CardInfo
                    itemId={itemId}
                    onNavigate={(trigger) => {
                        onNavigateInitiated(trigger, { position: trackingPosition });
                        onRecommendationClick(LotRecommendationTrigger.HouseNameClick);
                    }}
                />
                <div className="[grid-area:title]">
                    <ItemCardTitle
                        isHovered={linkHover}
                        itemId={itemId}
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                        onNavigate={() => {
                            onNavigateInitiated(ItemCardNavigationTrigger.TitleClick, { position: trackingPosition });
                            onRecommendationClick(LotRecommendationTrigger.TitleClick);
                        }}
                    />
                </div>
                <div className="[grid-area:cardhouseandrating]">
                    <CardHouseAndRating
                        itemId={itemId}
                        onNavigate={(trigger) => {
                            onNavigateInitiated(trigger, { position: trackingPosition });
                            onRecommendationClick(LotRecommendationTrigger.HouseNameClick);
                        }}
                    />
                </div>
                <CardPrice
                    enterLinkHoverState={enterLinkHoverState}
                    hideCardMenu={hideCardMenu}
                    isCardHovered={cardHover}
                    isLinkHovered={linkHover}
                    itemId={itemId}
                    leaveLinkHoverState={leaveLinkHoverState}
                    onNavigate={() => {
                        onNavigateInitiated(ItemCardNavigationTrigger.PriceClick, { position: trackingPosition });
                    }}
                />
            </section>
        </article>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: CategorySearchCard,
};

export default info;
